import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useTheme } from "../context/ThemeContext";

const POSTS = [
  {
    title: "Why AI Agents are your 2025 unfair advantage",
    excerpt: "From lead triage to order ops — ship workflows that run themselves.",
    cover: "/images/blog1.jpg",
    tags: ["AI", "Automation"],
    url: "#",
  },
  {
    title: "Design systems that actually scale",
    excerpt: "Tokens, theming, and guardrails for speed + consistency.",
    cover: "/images/blog2.jpg",
    tags: ["Design", "System"],
    url: "#",
  },
  {
    title: "Analytics you can act on",
    excerpt: "Cohorts, funnels, and clean event schemas for real decisions.",
    cover: "/images/blog3.jpg",
    tags: ["Analytics", "Growth"],
    url: "#",
  },
];

export default function Blog() {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const TOKENS = useMemo(
    () =>
      isDark
        ? { text: "#fff", muted: "rgba(255,255,255,.78)", panel: "rgba(12,18,30,.86)", ring: "rgba(255,255,255,.10)" }
        : { text: "#0B1220", muted: "rgba(17,24,39,.72)", panel: "#fff", ring: "rgba(2,6,23,.10)" },
    [isDark]
  );

  const [q, setQ] = useState("");
  const [tag, setTag] = useState("All");
  const tags = useMemo(() => ["All", ...Array.from(new Set(POSTS.flatMap(p => p.tags)))], []);
  const filtered = useMemo(() => {
    const base = tag === "All" ? POSTS : POSTS.filter(p => p.tags.includes(tag));
    const term = q.trim().toLowerCase();
    if (!term) return base;
    return base.filter(p => p.title.toLowerCase().includes(term) || p.excerpt.toLowerCase().includes(term));
  }, [q, tag]);

  return (
    <section className="relative min-h-[70vh] font-inter">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center">
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight">
            From the <span className="bg-clip-text text-transparent" style={{ backgroundImage: "linear-gradient(90deg, var(--brand,#1FA550), var(--accent,#06B6D4))" }}>blog</span>
          </h1>
          <p className="mt-3 text-lg" style={{ color: TOKENS.muted }}>
            Ideas on AI, design, and growth.
          </p>
        </div>

        <div className="mt-8 flex flex-col lg:flex-row gap-3 lg:gap-4 items-center">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search posts…"
            className="w-full lg:max-w-md rounded-xl px-4 py-3 outline-none ring-1"
            style={{ background: isDark ? "rgba(255,255,255,.06)" : "#fff", borderColor: TOKENS.ring, color: TOKENS.text }}
          />
          <nav className="flex flex-wrap gap-2 lg:ml-auto" aria-label="Tags">
            {tags.map((t) => {
              const active = tag === t;
              return (
                <button
                  key={t}
                  onClick={() => setTag(t)}
                  className="px-4 py-2 rounded-full text-sm font-semibold ring-1 transition"
                  style={{
                    borderColor: TOKENS.ring,
                    background: active ? "linear-gradient(135deg, var(--brand,#1FA550), var(--accent,#06B6D4))" : (isDark ? "rgba(255,255,255,.06)" : "#fff"),
                    color: active ? "#000" : TOKENS.muted,
                  }}
                >
                  {t}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((p, i) => (
            <motion.article
              key={p.title}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1], delay: i * 0.05 }}
              className="rounded-2xl overflow-hidden ring-1"
              style={{ background: TOKENS.panel, borderColor: TOKENS.ring }}
            >
              <div className="relative aspect-[16/10]">
                <img
                  src={p.cover}
                  alt={p.title}
                  className="absolute inset-0 h-full w-full object-cover"
                  loading="lazy"
                  onError={(e) => (e.currentTarget.src = "https://via.placeholder.com/900x560?text=Blog")}
                />
              </div>
              <div className="p-6">
                <div className="flex gap-2 mb-2">
                  {p.tags.map((t) => (
                    <span key={t} className="px-2 py-0.5 text-[11px] rounded-full ring-1"
                          style={{ borderColor: TOKENS.ring, color: TOKENS.muted }}>
                      {t}
                    </span>
                  ))}
                </div>
                <h3 className="text-lg font-semibold" style={{ color: TOKENS.text }}>{p.title}</h3>
                <p className="text-sm mt-1" style={{ color: TOKENS.muted }}>{p.excerpt}</p>
                <a href={p.url} className="mt-4 inline-flex items-center gap-2 text-sm font-semibold"
                   style={{ backgroundImage: "linear-gradient(90deg, var(--brand,#1FA550), var(--accent,#06B6D4))", WebkitBackgroundClip: "text", color: "transparent" }}>
                  Read more →
                </a>
              </div>
            </motion.article>
          ))}
        </div>
      </div>

      <style>{`.font-inter{font-family:Inter,ui-sans-serif,system-ui,-apple-system,'Segoe UI',Roboto,Helvetica,Arial}`}</style>
    </section>
  );
}
